import React from 'react';
import { COUNTRY_CODES, CountryCode } from '../../utils/constants/contact/countryCodes';

type SearchableCountryCodeProps = {
  value: string;
  onChange: (code: string) => void;
};

const SearchableCountryCode: React.FC<SearchableCountryCodeProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const selected = COUNTRY_CODES.find((c) => c.code === value) || COUNTRY_CODES[0];

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return COUNTRY_CODES;
    return COUNTRY_CODES.filter(
      (c) => c.country.toLowerCase().includes(q) || c.code.includes(q)
    );
  }, [search]);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (country: CountryCode) => {
    onChange(country.code);
    setIsOpen(false);
    setSearch('');
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className={`flex h-11 items-center gap-2 rounded-[8px] border bg-white px-3 font-description text-[14px] font-light text-[#212121] transition-colors focus:outline-none ${
          isOpen ? 'border-[#2776EA]' : 'border-[#E4E4E4]'
        } hover:border-[#B0B0B0]`}
      >
        <span className="truncate max-w-[120px]">{selected.country}</span>
        <span className="ml-1 shrink-0 text-[#919191]">{selected.code}</span>
        <svg
          className={`ml-auto h-4 w-4 shrink-0 text-[#919191] transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-[8px] border border-[#E4E4E4] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.1)] max-h-[280px] overflow-hidden flex flex-col">
          <div className="border-b border-[#E4E4E4] p-2">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search countries..."
              className="h-9 w-full rounded-[6px] border border-[#E4E4E4] bg-[#FAFAFA] px-3 font-description text-[13px] text-[#212121] placeholder:text-[#B0B0B0] focus:outline-none focus:border-[#2776EA]"
            />
          </div>
          <ul className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 font-description text-[13px] text-[#919191]">
                No countries found
              </li>
            )}
            {filtered.map((c) => (
              <li key={`${c.code}-${c.country}`}>
                <button
                  type="button"
                  onClick={() => handleSelect(c)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left font-description text-[13px] transition-colors hover:bg-[#F0F7FF] ${
                    c.code === value ? 'bg-[#F0F7FF] text-[#2776EA]' : 'text-[#212121]'
                  }`}
                >
                  <span className="truncate flex-1">{c.country}</span>
                  <span className="shrink-0 text-[#919191]">{c.code}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableCountryCode;
